import { Wildfire } from 'features/wildfires/wildfire.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatNumber } from 'shared/utils';

type UseWildfireProgressFormFieldChangesProps = {
  wildfire: Wildfire;
};

type FormValues = {
  acreage: number | null;
  containment: number | null;
  isFps: boolean;
};

type ChangeKey = keyof FormValues;

type ChangeMap = Partial<Record<ChangeKey, string>>;

const formatAcreage = (acreage: number | null): string =>
  acreage == null ? String(acreage) : formatNumber(acreage);

export const useWildfireProgressFormFieldChanges = (
  props: UseWildfireProgressFormFieldChangesProps,
): ChangeMap => {
  const { wildfire } = props;
  const { t } = useTranslation();

  const [acreage, containment, isFps] = useWatch<FormValues>({
    name: ['acreage', 'containment', 'isFps'],
  }) as [number | null, number | null, boolean];

  const acreageChanged = wildfire.data.acreage !== acreage;
  const containmentChanged = wildfire.data.containment !== containment;
  const fpsChanged = Boolean(wildfire.data.isFps) !== isFps;

  const changes: ChangeMap = {};
  if (acreageChanged) {
    changes.acreage = t('geoEventChangelog.acreage', {
      prevValue: formatAcreage(wildfire.data.acreage),
      newValue: formatAcreage(acreage),
    });
  }
  if (containmentChanged) {
    changes.containment = t('geoEventChangelog.containment', {
      prevValue: String(wildfire.data.containment),
      newValue: String(containment),
    });
  }
  if (fpsChanged) {
    changes.isFps = t('addIncidentReport.forwardProgressStopped');
  }

  return changes;
};
